import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ActionDetail, AreaListItem, ContactPickItem, ForesterCandidate, ListResult } from '@tww/shared';
import { AREA_STATUS_LABELS, actionCode, fmtDate, fmtEur, fmtHa, guidesNeeded, guidesText, ratingWarning, seasonLabel, seasonOfDate, todayIso } from '@tww/shared';
import { api, ApiRequestError, qs } from '../../api/client';
import { useLookup } from '../../api/hooks';
import { useAuth } from '../../auth/AuthProvider';
import { ContactPicker } from '../../ui/ContactPicker';
import { ContactQuickCreate } from '../contacts/ContactQuickCreate';
import { Note, Skeleton } from '../../ui/basics';
import { ConfirmDialog } from '../../ui/Modal';
import { ComboField, DateInput, Field, NumberInput, Select, TextInput } from '../../ui/fields';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/Toast';

const KEY = 'tww-wizard-v1';
interface W {
  step: 1 | 2 | 3 | 4;
  partner: ContactPickItem | null;
  type_code: string;
  state_code: string;
  region: string;
  season_code: string;
  year: number | null;
  trees: number | null;
  participants: number | null;
  donation: number | null;
  forester: ForesterCandidate | null;
  area_id: number | null;
  visit_date: string;
  visit_mode: 'jetzt' | 'spaeter';
  date: string;
}
const WINDOW_KEY: Record<string, string> = { F: 'fruehjahr', S: 'sommer', H: 'herbst' };
const fresh = (): W => ({ step: 1, partner: null, type_code: 'PFM_TN', state_code: '', region: '', season_code: seasonOfDate(todayIso()) === 'F' ? 'F' : 'H', year: new Date().getFullYear(), trees: null, participants: null, donation: null, forester: null, area_id: null, visit_date: '', visit_mode: 'spaeter', date: '' });
function load(): W {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw) return { ...fresh(), ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return fresh();
}

/** Assistent „Neue Aktion anlegen“ in vier Schritten (Plan 04 Abschnitt 5.4, Prototyp Screen 05). Zustand bleibt beim Neuladen erhalten. */
export function ActionWizardPage() {
  const nav = useNavigate();
  const toast = useToast();
  const { me } = useAuth();
  const [sp] = useSearchParams();
  const [w, setW] = useState<W>(load);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmCancel, setConfirmCancel] = useState(false);
  const set = (p: Partial<W>) => setW((s) => ({ ...s, ...p }));
  useEffect(() => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(w));
    } catch {
      /* ignore */
    }
  }, [w]);
  const states = useLookup('states');
  const regions = useLookup('regions');
  const types = useLookup('action_types');
  const windows = (me?.settings?.season_windows as Record<string, string> | undefined) ?? {};
  const rule = (me?.settings?.planting_guides_rule as { participants_per_guide: number; tww_guides: number } | undefined) ?? { participants_per_guide: 10, tww_guides: 1 };
  const guides = guidesNeeded(w.participants, rule);
  const regionOptions = useMemo(() => regions.values.filter((r) => !w.state_code || (r.meta as { state?: string } | null)?.state === w.state_code || !(r.meta as { state?: string } | null)?.state), [regions.values, w.state_code]);
  // Förster aus der Kontaktakte vorbelegen (?foerster=ID)
  const presetForester = sp.get('foerster');
  const foresters = useQuery({ queryKey: ['foresters', w.state_code, w.region], queryFn: () => api.get<{ items: ForesterCandidate[] }>(`/actions/foresters${qs({ state: w.state_code, region: w.region })}`).then((r) => r.items), enabled: w.step >= 2 || !!presetForester });
  useEffect(() => {
    if (presetForester && foresters.data && !w.forester) {
      const f = foresters.data.find((x) => x.id === Number(presetForester));
      if (f) set({ forester: f, state_code: w.state_code || f.state_code || '', region: w.region || f.region || '' });
    }
  }, [presetForester, foresters.data]);
  const areas = useQuery({ queryKey: ['areas-pool', w.region, w.state_code], queryFn: () => api.get<ListResult<AreaListItem>>(`/areas?${new URLSearchParams({ ...(w.region ? { 'filter[region]': w.region } : w.state_code ? { 'filter[state]': w.state_code } : {}), 'filter[group]': 'pool', pageSize: '20' })}`), enabled: w.step >= 2 && !!(w.region || w.state_code) });
  const prefix = String((types.values.find((t) => t.code === w.type_code)?.meta as { prefix?: string } | null)?.prefix ?? 'PFM');
  const area = areas.data?.items.find((a) => a.id === w.area_id) ?? null;
  const code = actionCode({ prefix, date: w.date || null, partner: w.partner?.display_name ?? 'Partner', ort: area?.city || area?.municipality || w.region || 'Ort' });
  const create = useMutation({
    mutationFn: (asLead: boolean) =>
      api.post<ActionDetail>('/actions', {
        partner_contact_id: w.partner!.id,
        type_code: w.type_code,
        state_code: w.state_code || null,
        region: w.region || null,
        season_code: w.season_code || null,
        year: w.year,
        trees_planned: w.trees,
        participants_planned: w.participants,
        donation_pledged: w.donation,
        forester_contact_id: asLead ? null : (w.forester?.id ?? null),
        area_id: asLead ? null : w.area_id,
        date: asLead ? null : w.date || null,
        site_visit_date: asLead ? null : w.visit_date || null,
      }),
    onSuccess: (a, asLead) => {
      sessionStorage.removeItem(KEY);
      toast(asLead ? 'Lead gespeichert' : `Aktion ${a.code} angelegt`);
      if (!asLead && w.visit_mode === 'jetzt' && a.site_visit) nav(`/protokoll/${a.site_visit.id}`);
      else nav(`/aktionen/${a.id}`);
    },
    onError: (e: Error) => {
      if (e instanceof ApiRequestError && e.fields) setErrors(e.fields);
      toast(e.message, 'error');
    },
  });
  const cancel = () => {
    sessionStorage.removeItem(KEY);
    nav('/aktionen');
  };
  // Rückfrage nur, wenn schon etwas eingegeben ist
  const f0 = fresh();
  const touched = w.step > 1 || !!w.partner || !!w.state_code || !!w.region || w.trees != null || w.participants != null || w.donation != null || w.type_code !== f0.type_code || w.season_code !== f0.season_code || w.year !== f0.year;
  const step1ok = !!w.partner && !!w.state_code;
  const step1missing = [!w.partner && 'Spendenpartner', !w.state_code && 'Bundesland'].filter(Boolean) as string[];
  const windowText = windows[WINDOW_KEY[w.season_code] ?? ''];
  const steps = ['Auftrag des Spendenpartners', 'Forstkontakt finden', 'Begehung', 'Termin und Anlegen'];
  return (
    <>
      <div className="crumb">
        <Link to="/aktionen">Aktionen</Link>
      </div>
      <div className="head">
        <div>
          <h1>Neue Aktion anlegen</h1>
        </div>
        <div className="actions">
          <button type="button" className="btn" onClick={() => (touched ? setConfirmCancel(true) : cancel())}>
            Abbrechen
          </button>
        </div>
      </div>
      <ol className="wiz-steps">
        {steps.map((s, i) => (
          <li key={s} className={w.step === i + 1 ? 'on' : w.step > i + 1 ? 'done' : ''}>
            <button type="button" onClick={() => w.step > i + 1 && set({ step: (i + 1) as W['step'] })} disabled={w.step <= i + 1}>
              <span className="n">{w.step > i + 1 ? <Icon name="check" /> : i + 1}</span> {s}
            </button>
          </li>
        ))}
      </ol>

      {w.step === 1 && (
        <div className="page-form">
          <div className="section">
            <div className="sh">
              <h2>Auftrag des Spendenpartners</h2>
            </div>
            <div className="sb">
              <div className="form">
                <ContactPicker label="Spendenpartner" required value={w.partner} onChange={(c) => set({ partner: c })} category="partner" error={errors.partner_contact_id} />
                <Select label="Aktionstyp" required value={w.type_code} onChange={(v) => set({ type_code: v })} options={types.values.map((t) => ({ value: t.code, label: t.label }))} placeholder="wählen" />
                <Select label="Bundesland" required error={errors.state_code} value={w.state_code} onChange={(v) => set({ state_code: v, region: '' })} options={states.values.map((s) => ({ value: s.code, label: s.label }))} placeholder="wählen" />
                <ComboField label="Region" value={w.region} onChange={(v) => set({ region: v })} error={errors.region} options={regionOptions.map((r) => ({ value: r.code, label: r.label }))} newHint="wird als neue Region gespeichert" />
                <Select label="Saison" value={w.season_code} onChange={(v) => set({ season_code: v })} options={[{ value: 'F', label: 'Frühjahr' }, { value: 'S', label: 'Sommer' }, { value: 'H', label: 'Herbst' }]} placeholder="wählen" />
                <NumberInput label="Jahr" value={w.year} onChange={(v) => set({ year: v })} />
                <NumberInput label="Anzahl Bäume" value={w.trees} onChange={(v) => set({ trees: v })} />
                <NumberInput label="Teilnehmende ca." value={w.participants} onChange={(v) => set({ participants: v })} hint={w.participants ? guidesText(guides) : undefined} />
                <NumberInput label="Spendenzusage (€)" value={w.donation} onChange={(v) => set({ donation: v })} decimals={2} />
              </div>
            </div>
          </div>
          <div className="aside">
            <div className="section">
              <div className="sb summary-box">
                <strong>Schritt 1 von 4</strong>
                <dl>
                  <dt>Partner</dt>
                  <dd>{w.partner?.display_name ?? '–'}</dd>
                  <dt>Region</dt>
                  <dd>{[states.values.find((s) => s.code === w.state_code)?.label, w.region].filter(Boolean).join(', ') || '–'}</dd>
                  <dt>Saison</dt>
                  <dd>{seasonLabel(w.season_code, w.year)}</dd>
                </dl>
              </div>
            </div>
            <div className="actions-col">
              <button type="button" className="btn primary" disabled={!step1ok} onClick={() => set({ step: 2 })}>
                Weiter: Forstkontakt
              </button>
              <button type="button" className="btn" disabled={!step1ok || create.isPending} onClick={() => create.mutate(true)} title="Aktion im Status offen, noch ohne Förster und Fläche">
                Speichern als Lead
              </button>
              {step1missing.length > 0 && <span className="mut small">Es fehlt: {step1missing.join(', ')}</span>}
            </div>
          </div>
        </div>
      )}

      {w.step === 2 && (
        <div className="page-form">
          <div className="col">
            <div className="section">
              <div className="sh">
                <h2>Forstkontakte in {w.region || states.values.find((s) => s.code === w.state_code)?.label}</h2>
              </div>
              <div className="sb">
                {foresters.isLoading ? (
                  <Skeleton rows={3} />
                ) : (
                  <ForesterCards list={(foresters.data ?? []).filter((f) => !w.region || f.in_region)} selected={w.forester?.id ?? null} onSelect={(f) => set({ forester: f })} empty={`Keine Förster in der Region ${w.region || ''}`} />
                )}
                {w.region && (foresters.data ?? []).some((f) => !f.in_region) && (
                  <details className="fs" style={{ marginTop: 12 }}>
                    <summary>
                      Weitere Kontakte im Bundesland <span className="st">{(foresters.data ?? []).filter((f) => !f.in_region).length}</span>
                    </summary>
                    <div className="body">
                      <ForesterCards list={(foresters.data ?? []).filter((f) => !f.in_region)} selected={w.forester?.id ?? null} onSelect={(f) => set({ forester: f })} empty="" />
                    </div>
                  </details>
                )}
                <NewForester region={w.region} onCreated={() => foresters.refetch()} />
              </div>
            </div>
            {w.forester && (
              <div className="section">
                <div className="sh">
                  <h2>Vor dem Anruf bei {w.forester.display_name}</h2>
                </div>
                <div className="sb">
                  <ul className="applied">
                    <li>Baumpreis klären{w.forester.last_price ? ` (zuletzt ${fmtEur(w.forester.last_price.price)} in ${w.forester.last_price.year})` : ' (noch kein Preis hinterlegt)'}</li>
                    <li>{w.forester.contract_year ? `Projektvereinbarung liegt vor (${w.forester.contract_year})` : 'Projektvereinbarung ansprechen'}</li>
                    <li>Begehungstermin vereinbaren (Schritt 3)</li>
                    {w.forester.rating_reason && <li>Bewertung {w.forester.rating_grade}: {w.forester.rating_reason}</li>}
                  </ul>
                  {ratingWarning(w.forester.rating_grade, w.forester.rating_reason) && <Note kind="bad">{ratingWarning(w.forester.rating_grade, w.forester.rating_reason)}</Note>}
                </div>
              </div>
            )}
            {(areas.data?.items.length ?? 0) > 0 && (
              <div className="section">
                <div className="sh">
                  <h2>Poolflächen in der Region</h2>
                </div>
                <div className="sb">
                  <div className="cat-grid">
                    {areas.data!.items.map((a) => (
                      <label key={a.id} className={`cat-card ${w.area_id === a.id ? 'on' : ''}`}>
                        <input type="radio" name="area" checked={w.area_id === a.id} onChange={() => set({ area_id: a.id })} />
                        <span>
                          {a.name}
                          <span className="mut small" style={{ display: 'block' }}>
                            {[a.municipality, a.hectares ? `${fmtHa(a.hectares)} ha` : null, AREA_STATUS_LABELS[a.status]].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                      </label>
                    ))}
                    <label className={`cat-card ${w.area_id === null ? 'on' : ''}`}>
                      <input type="radio" name="area" checked={w.area_id === null} onChange={() => set({ area_id: null })} />
                      <span>noch keine Fläche</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="aside">
            <div className="section">
              <div className="sb summary-box">
                <strong>Schritt 2 von 4</strong>
                <dl>
                  <dt>Partner</dt>
                  <dd>{w.partner?.display_name}</dd>
                  <dt>Förster</dt>
                  <dd>{w.forester?.display_name ?? <span className="miss">noch nicht gewählt</span>}</dd>
                  <dt>Fläche</dt>
                  <dd>{area?.name ?? 'entsteht mit der Begehung'}</dd>
                </dl>
              </div>
            </div>
            <div className="actions-col">
              <button type="button" className="btn primary" disabled={!w.forester} onClick={() => set({ step: 3 })}>
                Weiter: Begehung
              </button>
              <button type="button" className="btn" onClick={() => set({ step: 3 })}>
                Ohne Förster weiter
              </button>
              <button type="button" className="btn" onClick={() => set({ step: 1 })}>
                Zurück
              </button>
            </div>
          </div>
        </div>
      )}

      {w.step === 3 && (
        <div className="page-form">
          <div className="section">
            <div className="sh">
              <h2>Begehung</h2>
              <span className="desc">Termin vor Ort mit {w.forester?.display_name ?? 'dem Förster'}</span>
            </div>
            <div className="sb">
              <div className="form">
                <DateInput label="Begehung am" value={w.visit_date} onChange={(v) => set({ visit_date: v })} />
                <div className="field">
                  <label>Protokoll</label>
                  <div className="seg" role="group" aria-label="Protokoll">
                    <button type="button" className={w.visit_mode === 'spaeter' ? 'on' : ''} onClick={() => set({ visit_mode: 'spaeter' })}>
                      später in der Aktionsakte
                    </button>
                    <button type="button" className={w.visit_mode === 'jetzt' ? 'on' : ''} disabled={!w.area_id} title={w.area_id ? undefined : 'Dafür im vorigen Schritt eine Fläche wählen'} onClick={() => set({ visit_mode: 'jetzt' })}>
                      Protokoll jetzt ausfüllen
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="aside">
            <div className="actions-col">
              <button type="button" className="btn primary" onClick={() => set({ step: 4 })}>
                Weiter: Termin
              </button>
              <button type="button" className="btn" onClick={() => set({ step: 2 })}>
                Zurück
              </button>
            </div>
          </div>
        </div>
      )}

      {w.step === 4 && (
        <div className="page-form">
          <div className="section">
            <div className="sh">
              <h2>Termin und Anlegen</h2>
              {windowText && <span className="desc">{`Zeitfenster ${seasonLabel(w.season_code, w.year)}: ${windowText}`}</span>}
            </div>
            <div className="sb">
              <div className="form">
                <DateInput label="Aktionstag" value={w.date} onChange={(v) => set({ date: v, year: v ? Number(v.slice(0, 4)) : w.year, season_code: v ? seasonOfDate(v) : w.season_code })} hint="Ohne Datum bleibt die Aktion in Abstimmung; die Fristen entstehen mit dem Termin" />
                <Field label="Kennung" hint="wird automatisch gebildet, bei Kollision mit Suffix" htmlFor="wiz-code">
                  <input id="wiz-code" value={code} readOnly />
                </Field>
              </div>
              <h3 style={{ marginTop: 20 }}>Das wird angelegt</h3>
              <ul className="applied">
                <li>
                  Aktion <strong>{code}</strong> ({types.values.find((t) => t.code === w.type_code)?.label}) in {seasonLabel(w.season_code, w.year)}
                  {w.date ? ` am ${fmtDate(w.date)}` : ''}
                </li>
                <li>Spendenpartner {w.partner?.display_name}{w.donation ? `, Zusage ${fmtEur(w.donation)}` : ''}</li>
                <li>{w.forester ? `Förster ${w.forester.display_name} im Punkt „Zuständiger Förster“` : 'Förster offen (Status „offen“ bis zur Zuordnung)'}</li>
                <li>{area ? `Fläche ${area.name}` : 'Fläche entsteht mit der Begehung'}</li>
                <li>Checkliste mit allen Punkten der Vorlage{w.date ? ' und Fristen relativ zum Aktionstag' : ' (Fristen folgen mit dem Termin)'}</li>
                <li>{w.visit_date ? `Begehung am ${fmtDate(w.visit_date)}${w.visit_mode === 'jetzt' && w.area_id ? ' mit Protokoll (öffnet sich gleich)' : ''}` : 'Begehung noch ohne Termin'}</li>
                <li>{w.participants ? guidesText(guides) : 'Pflanzlotsenbedarf nach Teilnehmerzahl'}</li>
                <li>Vertragsentwurf, E-Mails und Monitoring nach 2 und 5 Jahren werden vorgemerkt</li>
              </ul>
            </div>
          </div>
          <div className="aside">
            <div className="actions-col">
              <button type="button" className="btn primary" disabled={create.isPending} onClick={() => create.mutate(false)}>
                <Icon name="check" /> Aktion anlegen
              </button>
              <button type="button" className="btn" onClick={() => set({ step: 3 })}>
                Zurück
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmCancel && <ConfirmDialog title="Assistent abbrechen" text="Die bisherigen Eingaben gehen verloren." confirmLabel="Verwerfen" danger onConfirm={cancel} onClose={() => setConfirmCancel(false)} />}
    </>
  );
}

function ForesterCards({ list, selected, onSelect, empty }: { list: ForesterCandidate[]; selected: number | null; onSelect: (f: ForesterCandidate) => void; empty: string }) {
  if (!list.length) return empty ? <div className="empty">{empty}</div> : null;
  return (
    <div className="cat-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
      {list.map((f) => (
        <button type="button" key={f.id} className={`fcard ${selected === f.id ? 'on' : ''}`} onClick={() => onSelect(f)}>
          <span className="top">
            <span>
              <strong>{f.display_name}</strong>
              <span className="mut small" style={{ display: 'block' }}>{[f.organisation_name, f.city].filter(Boolean).join(' · ')}</span>
            </span>
          </span>
          <span className="meta">
            <span>{f.last_price ? `${fmtEur(f.last_price.price)} (${f.last_price.year})` : 'kein Baumpreis'}</span>
            <span className={f.contract_year ? 'ok' : 'miss'}>{f.contract_year ? `Vertrag ${f.contract_year}` : 'kein Vertrag'}</span>
            <span className={f.confirmations.total && f.confirmations.done < f.confirmations.total ? 'miss' : ''}>{f.confirmations.total ? (f.confirmations.done === f.confirmations.total ? 'Pflanzbestätigungen vollständig' : `${f.confirmations.total - f.confirmations.done} Pflanzbestätigung fehlt`) : 'noch keine Aktion'}</span>
            <span>{f.action_count} Aktionen{f.last_area_state ? ` · Fläche ${AREA_STATUS_LABELS[f.last_area_state as keyof typeof AREA_STATUS_LABELS] ?? f.last_area_state}` : ''}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

function NewForester({ region, onCreated }: { region: string; onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="btn sm" style={{ marginTop: 12 }} onClick={() => setOpen(true)}>
        <Icon name="plus" /> Neuen Forstkontakt anlegen
      </button>
      {open && <ContactQuickCreate category="forester" region={region} onClose={() => setOpen(false)} onCreated={() => (setOpen(false), onCreated())} />}
    </>
  );
}
